// this component is used only in Executive and CEO dashboard

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import _ from "lodash";
import { useEffect, useState } from "react";
import styles from "./index.module.scss";

import Spacer from "../../utils/spacer";
import BarChartStacked from "../BarChartStacked";
import { Metric, MetricsWrapper } from "../Metric";

import { useTranslation } from "react-i18next";
import * as icons from "../../config/Icons";

function CoachingHours({ data, country = false }) {
  const { t } = useTranslation();
  const [columnName, setColumnName] = useState("completion_datetime");
  const [sortOrder, setSortOrder] = useState("asc");
  const [overallData, setOverallData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);

  useEffect(() => {
    if (!_.isEmpty(data)) {
      setOverallData(data.overall);
      setTableData(data.table);
    }
  }, [data]);

  useEffect(() => {
    if (tableData) {
      let chart = [];
      tableData.forEach(item => {
        const obj = {
          label: country ? item.country_name : item.campaign_name,
          values: [
            getMinutes(item.plan),
            getMinutes(item.do),
            getMinutes(item.check),
            getMinutes(item.act),
          ],
          totalAverage: item.total,
        };
        chart.push(obj);
      });
      setBarChartData(chart);
    }
  }, [tableData]);

  const getMinutes = total => {
    const time = total;
    const actualTime = time.split(":");
    const totalMinutes =
      +actualTime[0] * 24 * 60 + +actualTime[1] * 60 + +actualTime[2];
    return totalMinutes + actualTime[3] / 60;
  };

  const tableHeader = [
    {
      name: `${country ? "country_name" : "campaign_name"}`,
      label: `${country ? t("userManagement.country") : t("dashboard.coach.campaign")}`,
      width: "30%",
    },
    { name: "plan", label: t("dashboard.coach.plan"), width: "12%" },
    { name: "do", label: t("dashboard.coach.do"), width: "12%" },
    { name: "check", label: t("dashboard.coach.check"), width: "12%" },
    { name: "act", label: t("dashboard.coach.act"), width: "12%" },
    { name: "total", label: t("dashboard.coach.total"), width: "15%" },
  ];

  const sortData = name => {
    setColumnName(name);
    const order = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(order);
  };

  return (
    <div className={`${styles.row} ${styles.noBorder}`}>
      <h4
        className={`${styles.sectionHead} ${styles.alignLeft} ${styles.bold}`}
      >
        {t("dashboard.executive.coachingHours")} <br />{" "}
        <span className={styles.info}>{t("dashboard.executive.overall")}</span>
      </h4>
      {data ? (
        <>
          <div className={styles.w75}>
            <MetricsWrapper dashboard={true}>
              <Metric
                color="#0A859A"
                label={t("dashboard.executive.totalCoachingHours")}
                data={
                  overallData ? overallData.total_coaching_hours : "00:00:00:00"
                }
                info="DD:HH:MM:SS"
              />
              <Metric
                color="#0A859A"
                label={t("dashboard.executive.avgCoachingHours")}
                data={overallData ? overallData.average_coaching_hours : "0"}
              />
            </MetricsWrapper>
            <h4
              className={`${styles.sectionHead} ${styles.alignLeft} ${styles.bold}`}
            >
              <span className={styles.info}> {t("dashboard.coach.total")}</span>{" "}
              <br />{" "}
              <span className={styles.info} style={{ fontSize: "10px" }}>
                DD:HH:MM:SS
              </span>
            </h4>
            <MetricsWrapper>
              <Metric
                fullWidth
                fontSize="20px"
                color="#0A859A"
                label={t("dashboard.executive.totalPlanDuration")}
                data={
                  overallData && _.has(overallData, "plan")
                    ? overallData.plan
                    : "00:00:00:00"
                }
              />
              <Metric
                fullWidth
                fontSize="20px"
                color="#17AECD"
                label={t("dashboard.executive.totalDoDuration")}
                data={
                  overallData && _.has(overallData, "do")
                    ? overallData.do
                    : "00:00:00:00"
                }
              />
              <Metric
                fullWidth
                fontSize="20px"
                color="#2FC8E6"
                label={t("dashboard.executive.totalCheckDuration")}
                data={
                  overallData && _.has(overallData, "check")
                    ? overallData.check
                    : "00:00:00:00"
                }
              />
              <Metric
                fullWidth
                fontSize="20px"
                color="#83DFF3"
                label={t("dashboard.executive.totalActDuration")}
                data={
                  overallData && _.has(overallData, "act")
                    ? overallData.act
                    : "00:00:00:00"
                }
              />
            </MetricsWrapper>
          </div>

          <TableContainer
            component={Paper}
            elevation={0}
            style={{ maxHeight: 350 }}
          >
            <Table aria-label="Team" className={styles.table} stickyHeader>
              <TableHead className={styles.tableHead}>
                <TableRow>
                  {tableHeader &&
                    tableHeader.map((tableHead, index) => (
                      <TableCell
                        key={index}
                        style={{ width: tableHead.width }}
                        className={styles.tableHeadCell}
                        onClick={() => {
                          sortData(tableHead.name);
                        }}
                      >
                        {tableHead.label}

                        {columnName === tableHead.name &&
                          sortOrder === "asc" && (
                            <span>
                              <KeyboardArrowUpIcon fontSize="small" />
                            </span>
                          )}
                        {columnName === tableHead.name &&
                          sortOrder === "desc" && (
                            <span>
                              <KeyboardArrowDownIcon fontSize="small" />
                            </span>
                          )}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData &&
                  tableData.map((item, index) => {
                    const flagIcon = country
                      ? item.country_name + "FlagIcon"
                      : "";
                    return (
                      <TableRow key={index} className={styles.tableRow}>
                        {tableHeader &&
                          tableHeader.map((tableHead, _index) => (
                            <TableCell key={_index} component="th" scope="row">
                              {country && tableHead.name === "country_name" && (
                                <img src={icons[flagIcon]} alt={item.label} />
                              )}
                              {item[tableHead.name]}
                            </TableCell>
                          ))}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <Spacer y={3} />
          <div className={styles.w50}>
            <h4
              className={`${styles.sectionHead} ${styles.alignLeft} ${styles.bold}`}
            >
              <span className={styles.info}> {t("dashboard.average")}</span>
            </h4>
            {barChartData && (
              <BarChartStacked
                colors={["#0A859A", "#17AECD", "#2FC8E6", "#83DFF3"]}
                fontColors={["#fff", "#fff", "#111", "#111"]}
                legends={[
                  t("dashboard.coach.plan"),
                  t("dashboard.coach.do"),
                  t("dashboard.coach.check"),
                  t("dashboard.coach.act"),
                ]}
                innerText={false}
                labelXSize="large"
                maxHeight="200px"
                data={barChartData}
                totalSuffix="mins"
                labelX={`${country
                  ? t("userManagement.country")
                  : t("dashboard.coach.campaign")
                  }`}
                labelY={t("dashboard.executive.avgCoachingHours")}
                showTooltip={true}
                country={country}
              />
            )}
          </div>
        </>
      ) : (
        <div>{t("dashboard.noDataYet")}</div>
      )}
    </div>
  );
}

export default CoachingHours;
